import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QrAuthRequest } from "src/types";
import { useCall } from "src/call-sdk/context/CallContext";
import { useToast } from "src/context/ToastContext";
import Messages from "src/config/messages.constants";
import { generateDeviceId, getDeviceId } from "src/lib/generateDeviceId";
import styled from "styled-components";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import { Typography } from "@mui/material";
import ErrorBoundary from "./ErrorBoundary"; // Import the ErrorBoundary component
import LocalStorage from "../call-sdk/lib/local-storage";
import StorageContants from "src/config/storage.constants";
import Config from "../config";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100dvh; /* 100% of the viewport height */
  background-color: white;
`;

const LogoContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 25px;
`;

const SpinnerContainer = styled.div`
  flex: 1; /* Allow the spinner container to take all available vertical space */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px; /* Add some bottom margin to create space at the bottom */
`;

const Text = styled(Typography)`
  font-size: 20px;
  font-weight: 800;
  margin-top: 20px;
`;

const Index = () => {
  const regionConfig = Config[`${process.env.REACT_APP_REGION}`];
  const { qr_code_id } = useParams();
  const isAuthQrFetchedRef = useRef(false);
  const {
    getAuthenticationToken,
    authLoginResponse,
    init,
    setBusinessDetails,
    refreshedAccessToken,
    isBusinessFetched,
    apiErrorFlag,
    business,
  } = useCall();
  const [businessId, setBusinessId] = useState<string | null>(null);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    LocalStorage.remove(StorageContants.IS_HU_USER);
  }, []);

  const startWebSocket = useCallback(
    (initialize: any) => {
      const deviceId = getDeviceId();
      init({
        userId: initialize.userId,
        deviceId,
        // On a WebSocket auth rejection, toast the server message and send the guest to
        // the terminal /qr-error ("please re-scan") page so the flow doesn't hang on
        // "Verifying…". forceShutdown has already stopped reconnects.
        onAuthFailure: (message) => {
          toast.error(message || Messages.SESSION_ERROR);
          //navigate({ pathname: "/qr-error" }, { replace: true });
        },
      });
    },
    [init, toast],
  );

  useEffect(() => {
    if (qr_code_id && !isAuthQrFetchedRef.current) {
      LocalStorage.set(StorageContants.QR_ID, qr_code_id);
      localStorage.removeItem("hasReloaded");
      const deviceid = generateDeviceId();

      let authRequestBody: QrAuthRequest = {
        qrCodeId: qr_code_id,
        deviceid,
      };
      getAuthenticationToken(authRequestBody);
      isAuthQrFetchedRef.current = true;
    }
  }, [getAuthenticationToken, qr_code_id]);

  useEffect(() => {
    if (authLoginResponse) {
      const { uuid, businessId } = authLoginResponse;

      let initialize = {
        userId: uuid,
      };
      startWebSocket(initialize);
      setBusinessId(businessId);
    }
  }, [authLoginResponse, startWebSocket]);

  useEffect(() => {
    if (refreshedAccessToken && businessId) {
      setBusinessDetails(businessId);
    }
  }, [refreshedAccessToken, businessId, setBusinessDetails, business]);

  // Redirect to "/qr-business" when isBusinessFetched becomes true
  useEffect(() => {
    if (isBusinessFetched) {
      navigate({ pathname: "/qr-business" }, { replace: true });
    }
  }, [isBusinessFetched, navigate]);

  useEffect(() => {
    if (apiErrorFlag || !qr_code_id) {
      console.log("REDIRECTING TO ERROR", { apiErrorFlag, qr_code_id });

      navigate({ pathname: "/qr-error" }, { replace: true });
    }
  }, [apiErrorFlag, navigate, qr_code_id]);

  return (
    <ErrorBoundary>
      {" "}
      {/* Wrap the content with ErrorBoundary */}
      <Container>
        <LogoContainer>
          <img
            src={regionConfig.appLogoImageSrc}
            alt="Logo"
            width="100%"
            height={regionConfig.appLogoImageHeight}
          />
        </LogoContainer>

        <SpinnerContainer>
          <Stack sx={{ color: "grey.500" }} spacing={2} direction="row">
            <CircularProgress color="success" />
          </Stack>
          <Text>Verifying...</Text>
        </SpinnerContainer>
      </Container>
    </ErrorBoundary>
  );
};

export default Index;
